import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import { HomeIcon, MailIcon, PhoneIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Button } from "~/components/ui/Button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { getLocale } from "~/lib/i18n.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData } from "~/lib/utils";

export const sitemap = createSitemapFunction("contact-us");

export const meta: MetaFunction = (data: any) => {
  const { t } = useTranslation();
  return getMetaData(data, {
    title: t("Contatti"),
    description: t(
      "Contatta il team di Chorus Wellness per informazioni, prenotazioni o qualsiasi altra richiesta. Siamo qui per aiutarti a trovare la tua dimensione di benessere."
    ),
  });
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "contatti";

    const locale = await getLocale(request);
    const lang = locale;
    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }
    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
        turnstileSiteKey: context.cloudflare.env.TURNSTILE_SITE_KEY ?? "",
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
        turnstileSiteKey: context.cloudflare.env.TURNSTILE_SITE_KEY ?? "",
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
      turnstileSiteKey: context.cloudflare.env.TURNSTILE_SITE_KEY ?? "",
    };
  }
}

export async function action({ request, context }: ActionFunctionArgs) {
  if (request.method !== "POST") {
    return Response.json({ error: "Method Not Allowed" }, { status: 405 });
  }

  try {
    const formData = await request.formData();
    const formId = formData.get("form-id") as string;
    const email = formData.get("email") as string;
    const name = formData.get("name") as string;
    const surname = formData.get("surname") as string;
    const message = formData.get("message") as string;
    const object = formData.get("object") as string;
    const privacy = formData.get("privacy") === "on";
    const unitTag = formData.get("_wpcf7_unit_tag") as string;
    const turnstileToken = formData.get("cf-turnstile-response") as string;

    const turnstileSecret = context.cloudflare.env.TURNSTILE_SECRET_KEY;
    if (turnstileSecret) {
      if (!turnstileToken) {
        return Response.json(
          {
            error: "Captcha verification required",
            fieldErrors: { turnstile: "Captcha verification required" },
          },
          { status: 400 }
        );
      }

      const verifyBody = new FormData();
      verifyBody.append("secret", turnstileSecret);
      verifyBody.append("response", turnstileToken);
      const remoteIp = request.headers.get("CF-Connecting-IP");
      if (remoteIp) {
        verifyBody.append("remoteip", remoteIp);
      }

      const verifyResp = await fetch(
        "https://challenges.cloudflare.com/turnstile/v0/siteverify",
        {
          method: "POST",
          body: verifyBody,
        }
      );
      const verifyJson: any = await verifyResp.json();
      if (!verifyJson.success) {
        return Response.json(
          {
            error: "Captcha verification failed",
            fieldErrors: { turnstile: "Captcha verification failed" },
          },
          { status: 400 }
        );
      }
    }

    const ContactFormSchema = Yup.object().shape({
      email: Yup.string().email("Invalid email").required("Required"),
      name: Yup.string().required("Required"),
      surname: Yup.string().required("Required"),
      message: Yup.string().required("Required"),
      object: Yup.string().required("Required"),
      unitTag: Yup.string().required("Required"),
      privacy: Yup.boolean().oneOf([true], "You must agree to privacy policy"),
    });

    try {
      await ContactFormSchema.validate({
        name,
        surname,
        email,
        message,
        object,
        unitTag,
        privacy,
      });
    } catch (validationError: any) {
      return Response.json(
        {
          error: "Validation failed",
          fieldErrors: { [validationError.path]: validationError.message },
        },
        { status: 400 }
      );
    }

    const contactFormResp = await fetch(
      `${context.cloudflare.env.WP_API_HOST}wp-json/contact-form-7/v1/contact-forms/${formId}/feedback`,
      {
        method: "POST",
        body: formData,
      }
    );

    const contactFormRespJson: any = await contactFormResp.json();

    if (contactFormResp.ok && contactFormRespJson.status === "mail_sent") {
      return Response.json({
        error: null,
        message: "Message sent successfully",
        sent: true,
      });
    } else {
      console.error("Error: ", contactFormRespJson);
      return Response.json({
        error: "An Error happened sending the form data",
      });
    }
  } catch (err: any) {
    console.error("Error: ", err);
  }
  return Response.json({
    error: null,
    fieldErrors: {
      name: null,
      surname: null,
      email: null,
      object: null,
      message: null,
    },
  });
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export default function ContactUs() {
  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<any>();
  const navigation = useNavigation();
  const { t, i18n } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileSiteKey = loaderData.turnstileSiteKey;

  useEffect(() => {
    if (actionData?.sent) {
      setShowSuccess(true);
    } else {
      setShowSuccess(false);
    }
  }, [actionData]);

  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (!turnstileSiteKey) return;

    const scriptId = "cf-turnstile-script";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    const renderWidget = () => {
      if (
        !window.turnstile ||
        !turnstileRef.current ||
        turnstileWidgetIdRef.current
      ) {
        return;
      }
      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileRef.current,
        {
          sitekey: turnstileSiteKey,
        }
      );
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else if (window.turnstile) {
      renderWidget();
    } else {
      script.addEventListener("load", renderWidget);
    }

    return () => {
      if (window.turnstile && turnstileWidgetIdRef.current) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch {}
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey]);

  useEffect(() => {
    if (
      actionData?.sent &&
      window.turnstile &&
      turnstileWidgetIdRef.current
    ) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
  }, [actionData?.sent]);

  return (
    <div
      className={`
        relative
      `}
    >
      <main className="max-w-7xl mx-auto flex flex-col pt-32 pb-12 lg:pb-0 lg:pt-12">
        {loaderData.page.acf && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 px-4">
            <div className="flex flex-col">
              <h2 className="text-2xl">{loaderData.page.acf.title}</h2>

              <Form method="post" className="grid grid-cols-2 gap-4 mt-8">
                <input
                  type="hidden"
                  name="form-id"
                  value={loaderData.page.acf.contact_form_id}
                />
                <input
                  type="hidden"
                  name="_wpcf7_unit_tag"
                  value={`wpcf7-f-${
                    loaderData.page.acf.contact_form_id
                  }-${Math.random().toString(36).substring(7).toUpperCase()}`}
                />
                {actionData?.error && (
                  <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 w-full col-span-2">
                    <p className="text-destructive text-sm text-center">
                      {actionData.error}
                    </p>
                  </div>
                )}

                {showSuccess && (
                  <div className="mt-4 p-3 bg-green-200 w-full col-span-2">
                    <p className="text-green-600 text-sm text-center">
                      {t("Messaggio inviato con successo")}
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-medium">
                    {t("Nome")}
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t("Es.Mario") + "*"}
                    required
                  />
                  {actionData?.fieldErrors?.name && (
                    <div className="text-destructive text-sm mt-1">
                      {actionData.fieldErrors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="surname"
                    className="block text-sm font-medium"
                  >
                    {t("Cognome")}
                  </label>
                  <Input
                    id="surname"
                    name="surname"
                    type="text"
                    placeholder={t("Es.Rossi" + "*")}
                    required
                  />
                  {actionData?.fieldErrors?.surname && (
                    <div className="text-destructive text-sm mt-1">
                      {actionData.fieldErrors.surname}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Email*"
                    required
                    autoComplete="email"
                  />
                  {actionData?.fieldErrors?.email && (
                    <div className="text-destructive text-sm mt-1">
                      {actionData.fieldErrors.email}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="object" className="block text-sm font-medium">
                    {t("Sono interessato/a a")}:
                  </label>
                  <Select name="object" required>
                    <SelectTrigger>
                      <SelectValue placeholder={t("Es.Massaggi") + "*"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="massaggi">{t("Massaggi")}</SelectItem>
                      <SelectItem value="esperienze">
                        {t("Esperienze")}
                      </SelectItem>
                      <SelectItem value="altro">{t("Altro")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {actionData?.fieldErrors?.object && (
                    <div className="text-destructive text-sm mt-1">
                      {actionData.fieldErrors.object}
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium"
                  >
                    {t("Sono interessato/a a")}:
                  </label>
                  <Textarea
                    name="message"
                    id="message"
                    required
                    rows={4}
                    placeholder={
                      t("es. ciao, vorrei più informazioni sui massaggi") + "*"
                    }
                  />
                  {actionData?.fieldErrors?.message && (
                    <div className="text-destructive text-sm mt-1">
                      {actionData.fieldErrors.message}
                    </div>
                  )}
                </div>

                <div className="col-span-2 flex flex-row items-center space-x-2">
                  <Checkbox name="privacy" id="privacy" required />
                  <label
                    htmlFor="privacy"
                    className="block text-sm font-medium"
                  >
                    {t("Inviando il messaggio accetto la")}{" "}
                    <Link
                      target="_blank"
                      className="text-secondary underline"
                      to={getMenuHref(i18n.language, "/privacy-policy")}
                    >
                      privacy policy
                    </Link>
                  </label>
                </div>

                {turnstileSiteKey && (
                  <div className="col-span-2 flex flex-col">
                    <div ref={turnstileRef} />
                    {actionData?.fieldErrors?.turnstile && (
                      <div className="text-destructive text-sm mt-1">
                        {actionData.fieldErrors.turnstile}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  variant={"secondary"}
                  className="mt-4 w-full"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {t("Invia messaggio")}
                </Button>
              </Form>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-col">
                <div className="flex flex-row">
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf.image.id}`}
                    className="w-[16rem] lg:w-[21rem] h-32 lg:h-52 object-cover object-center"
                  />
                </div>
                <div className="flex flex-row justify-end -mt-12 lg:-mt-32">
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf.image_2.id}`}
                    className="w-[16rem] lg:w-[21rem] h-32 lg:h-52 object-cover object-center"
                  />
                </div>
              </div>

              <div className="flex flex-col mt-12">
                <h6 className="font-bold text-lg">{t("Vienici a trovare")}:</h6>

                <Link
                  to={loaderData.page.acf.position?.address?.url}
                  className="flex flex-row items-center space-x-2"
                >
                  <HomeIcon size={14} />
                  <span>{loaderData.page.acf.position?.address?.title}</span>
                </Link>
              </div>

              <div className="flex flex-col mt-4">
                <h6 className="font-bold text-lg">{t("Altri contatti")}:</h6>
                <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2">
                  <Link
                    to={loaderData.page.acf.contacts?.booking?.url}
                    className="flex flex-row items-center space-x-2"
                  >
                    <MailIcon size={14} />
                    <span>{loaderData.page.acf.contacts?.booking?.title}</span>
                  </Link>
                  <Link
                    to={loaderData.page.acf.contacts?.telephone?.url}
                    className="flex flex-row items-center space-x-2"
                  >
                    <PhoneIcon size={14} />
                    <span>
                      {loaderData.page.acf.contacts?.telephone?.title}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
